// HTTP error shapes used by `mapToAppError` and any caller that needs to
// narrow caught values before they're mapped. Lives in `error-handling`
// rather than `http-client` so the two packages do not form a workspace
// cycle: `mapToAppError` (here) needs to recognize `HttpError`, and the
// HTTP client throws values shaped like `HttpError` while also calling
// `mapToAppError` to wrap caught errors. Putting the shared shapes in
// the leaf package keeps the platform graph acyclic
// (`http-client → error-handling`, not the reverse).

export interface HttpError {
  status: number
  data: unknown
  message: string
}

export function isHttpError(value: unknown): value is HttpError {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as { status?: unknown }).status === 'number' &&
    'data' in (value as object) &&
    typeof (value as { message?: unknown }).message === 'string'
  )
}

export interface ProblemDetails {
  /** A URI reference that identifies the specific occurrence of the problem (e.g. request/tracing ID) */
  traceId: string
  /** A URI reference [RFC3986] that identifies the problem type */
  type: string
  /** A short, human-readable summary of the problem type */
  title: string
  /** A human readable title. */
  friendlyTitle?: string
  /** The HTTP status code [RFC7231] */
  status: number
  /** A human-readable explanation specific to this occurrence of the problem */
  detail?: string
  /** A URI reference that identifies the specific occurrence of the problem (e.g. request/tracing ID) */
  instance?: string
  /** A list of errors that occurred during the request */
  errors?: Record<string, string[]>
  [key: string]: unknown
}

export function isProblemDetails(value: unknown): value is ProblemDetails {
  if (typeof value !== 'object' || value === null) return false
  const candidate = value as Partial<ProblemDetails>
  return (
    typeof candidate.type === 'string' &&
    typeof candidate.title === 'string' &&
    typeof candidate.status === 'number' &&
    typeof candidate.traceId === 'string'
  )
}
